import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LogOut, RefreshCw, Pencil, Save, X, Star, Check, Trash2, Package, MessageSquareQuote, Info, ImageIcon, Images, Upload, Plus, DollarSign, RotateCcw, Search,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { API } from "../lib/site";
import { fileToCompressedDataUrl } from "../lib/image";

const STATUS_COLORS = {
  RECEIVED: "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30",
  DIAGNOSING: "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/30",
  "WAITING ON PARTS": "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/30",
  "IN REPAIR": "bg-[#0055FF]/15 text-[#0055FF] border-[#0055FF]/30",
  "READY FOR PICKUP": "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30",
  "SHIPPED BACK": "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30",
  COMPLETED: "bg-zinc-500/15 text-zinc-300 border-zinc-700",
  CANCELLED: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30",
};

function LogoMark({ size = "h-9" }) {
  return <img src="/logo.png" alt="Tombs Electronic Repair" className={`${size} w-auto object-contain`} />;
}

function Stars({ value = 5 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= value ? "fill-[#FFCC00] text-[#FFCC00]" : "text-zinc-700"}`} />
      ))}
    </div>
  );
}

export default function AdminPage() {
  // Token is NOT auto-filled from storage. We only attempt a silent re-auth on mount
  // if a previously-remembered token exists, then the input stays blank for fresh logins.
  const [token, setToken] = useState("");
  const [rememberToken, setRememberToken] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [envInfo, setEnvInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [showTokenHelp, setShowTokenHelp] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [selectedReviews, setSelectedReviews] = useState(new Set());
  const [bookingSearch, setBookingSearch] = useState("");
  const [reviewSearch, setReviewSearch] = useState("");

  const apiAuth = (extra = {}) => ({ headers: { "X-Admin-Token": token, ...extra } });

  const verify = async (t) => {
    try {
      await axios.get(`${API}/admin/verify`, { headers: { "X-Admin-Token": t } });
      return true;
    } catch {
      return false;
    }
  };

  const login = async () => {
    if (!token.trim()) return;
    const trimmed = token.trim();
    const ok = await verify(trimmed);
    if (ok) {
      if (rememberToken) {
        localStorage.setItem("tombs_admin_token", trimmed);
      } else {
        // Ensure no stale token from a previous "remember me" session lingers.
        localStorage.removeItem("tombs_admin_token");
      }
      setAuthed(true);
      toast.success("Welcome back");
    } else {
      toast.error("Invalid admin token");
    }
  };

  const logout = () => {
    localStorage.removeItem("tombs_admin_token");
    setToken("");
    setRememberToken(false);
    setAuthed(false);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [b, s, r, g, e] = await Promise.all([
        axios.get(`${API}/admin/bookings`, apiAuth()),
        axios.get(`${API}/admin/statuses`, apiAuth()),
        axios.get(`${API}/admin/reviews`, apiAuth()),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/admin/env`, apiAuth()),
      ]);
      setBookings(b.data);
      setStatuses(s.data.statuses);
      setReviews(r.data);
      setGallery(g.data);
      setEnvInfo(e.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const remembered = localStorage.getItem("tombs_admin_token");
      if (remembered) {
        const ok = await verify(remembered);
        if (ok) {
          setToken(remembered);
          setRememberToken(true);
          setAuthed(true);
        } else {
          // Clear obsolete/invalid remembered tokens
          localStorage.removeItem("tombs_admin_token");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const startEdit = (b) => {
    setEditing(b.ticket_id);
    setEditStatus(b.status);
    setEditNotes(b.notes || "");
  };

  const saveEdit = async () => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${editing}`,
        { status: editStatus, notes: editNotes },
        apiAuth()
      );
      toast.success(`${editing} updated`);
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const setApproval = async (id, approved) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, { approved }, apiAuth());
      toast.success(approved ? "Review approved" : "Review unpublished");
      loadAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, apiAuth());
      toast.success("Review deleted");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ----- Bulk selection helpers -----
  const toggleBookingSelected = (ticket_id) => {
    setSelectedBookings((prev) => {
      const next = new Set(prev);
      if (next.has(ticket_id)) next.delete(ticket_id);
      else next.add(ticket_id);
      return next;
    });
  };
  const toggleReviewSelected = (id) => {
    setSelectedReviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllBookings = () => {
    const visibleIds = filteredBookings.map((b) => b.ticket_id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedBookings.has(id));
    if (allVisibleSelected) {
      setSelectedBookings((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedBookings((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const selectAllReviews = () => {
    const visibleIds = [...filteredPending, ...filteredPublished].map((r) => r.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedReviews.has(id));
    if (allVisibleSelected) {
      setSelectedReviews((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedReviews((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const deleteBooking = async (ticket_id) => {
    if (!window.confirm(`Permanently delete ticket ${ticket_id}? This also removes related payment records.`)) return;
    try {
      await axios.delete(`${API}/admin/bookings/${ticket_id}`, apiAuth());
      toast.success(`${ticket_id} deleted`);
      setSelectedBookings((prev) => {
        const n = new Set(prev);
        n.delete(ticket_id);
        return n;
      });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const bulkDeleteBookings = async () => {
    const ids = Array.from(selectedBookings);
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} ticket${ids.length > 1 ? "s" : ""}? This also removes related payment records.`)) return;
    try {
      const res = await axios.post(`${API}/admin/bookings/bulk-delete`, { ids }, apiAuth());
      toast.success(`Deleted ${res.data.deleted} ticket${res.data.deleted !== 1 ? "s" : ""}`);
      setSelectedBookings(new Set());
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  const bulkDeleteReviews = async () => {
    const ids = Array.from(selectedReviews);
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} review${ids.length > 1 ? "s" : ""}?`)) return;
    try {
      const res = await axios.post(`${API}/admin/reviews/bulk-delete`, { ids }, apiAuth());
      toast.success(`Deleted ${res.data.deleted} review${res.data.deleted !== 1 ? "s" : ""}`);
      setSelectedReviews(new Set());
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  // ----- Refund -----
  const [refundFor, setRefundFor] = useState(null); // {ticket_id, txn}
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const DIAGNOSTIC_FEE = 15;

  const openRefund = async (ticket_id) => {
    try {
      const res = await axios.get(`${API}/admin/payments/by-ticket/${ticket_id}`, apiAuth());
      const paidTxn = (res.data || []).find((t) => t.payment_status === "paid" && t.refund_status !== "completed");
      if (!paidTxn) {
        toast.error("No refundable paid payment on this ticket.");
        return;
      }
      // Diagnostic fee only applies to ship-in repairs (where the deposit was mandatory).
      const booking = bookings.find((b) => b.ticket_id === ticket_id);
      const isShipIn = (booking?.delivery_method || "").toLowerCase() === "ship-in";
      const fee = isShipIn ? DIAGNOSTIC_FEE : 0;
      const defaultRefund = Math.max(0, paidTxn.amount_usd - fee).toFixed(2);
      setRefundFor({ ticket_id, txn: paidTxn, isShipIn });
      setRefundAmount(defaultRefund);
      setRefundReason(isShipIn
        ? "Customer declined repair after diagnostic."
        : "Refund issued.");
    } catch {
      toast.error("Could not load payment");
    }
  };

  const sendRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (Number.isNaN(amt) || amt < 0) return toast.error("Enter a valid amount");
    if (!refundFor) return;
    const exceeds = amt > refundFor.txn.amount_usd;
    if (exceeds) {
      const ok = window.confirm(
        `You're refunding $${amt.toFixed(2)} but the customer only paid $${refundFor.txn.amount_usd.toFixed(2)}.\n\n` +
        `Stripe can only refund up to $${refundFor.txn.amount_usd.toFixed(2)}. ` +
        `The extra $${(amt - refundFor.txn.amount_usd).toFixed(2)} will be recorded as owed manually — ` +
        `you'll need to send that part via Venmo / cash / a separate Stripe transfer.\n\nProceed?`
      );
      if (!ok) return;
    }
    setRefundBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/payments/refund`,
        {
          ticket_id: refundFor.ticket_id,
          amount: amt,
          reason: refundReason.trim() || null,
          transaction_id: refundFor.txn.id,
        },
        apiAuth()
      );
      const { refund_status, manual_owed_usd = 0, stripe_refund_usd = amt } = res.data;
      if (refund_status === "completed") {
        if (manual_owed_usd > 0) {
          toast.success(`Stripe refunded $${stripe_refund_usd.toFixed(2)} · $${manual_owed_usd.toFixed(2)} owed manually`);
        } else {
          toast.success(`Refund of $${amt.toFixed(2)} sent`);
        }
      } else {
        toast.success(`Refund of $${amt.toFixed(2)} recorded · finalize in Stripe Dashboard`);
      }
      setRefundFor(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setRefundBusy(false);
    }
  };

  // ----- Invoice (Stripe) -----
  const [invoiceFor, setInvoiceFor] = useState(null); // ticket_id
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceLabel, setInvoiceLabel] = useState("");
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [ticketPayments, setTicketPayments] = useState([]);

  const openInvoice = async (ticket_id) => {
    setInvoiceFor(ticket_id);
    setInvoiceAmount("");
    setInvoiceLabel("Repair quote");
    try {
      const res = await axios.get(`${API}/admin/payments/by-ticket/${ticket_id}`, apiAuth());
      setTicketPayments(res.data || []);
    } catch {
      setTicketPayments([]);
    }
  };

  const sendInvoice = async () => {
    const amt = parseFloat(invoiceAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!invoiceLabel.trim()) return toast.error("Add a short label");
    setInvoiceBusy(true);
    try {
      await axios.post(
        `${API}/admin/payments/invoice`,
        {
          ticket_id: invoiceFor,
          amount: amt,
          label: invoiceLabel.trim(),
          origin_url: window.location.origin,
        },
        apiAuth()
      );
      toast.success(`Invoice for $${amt.toFixed(2)} emailed to customer`);
      setInvoiceFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invoice");
    } finally {
      setInvoiceBusy(false);
    }
  };

  const [syncBusy, setSyncBusy] = useState(false);
  const syncTicketPayments = async (ticket_id) => {
    setSyncBusy(true);
    try {
      const res = await axios.post(`${API}/admin/payments/sync/${ticket_id}`, {}, apiAuth());
      const { checked = 0, refreshed = 0, newly_paid = 0 } = res.data || {};
      if (newly_paid > 0) {
        toast.success(`${newly_paid} payment${newly_paid > 1 ? "s" : ""} just marked paid`);
      } else if (refreshed > 0) {
        toast.success("Payment statuses updated");
      } else if (checked === 0) {
        toast(`No pending payments to check on ${ticket_id}`);
      } else {
        toast(`Already up to date — Stripe shows no new paid payments`);
      }
      // Refresh both the dialog list and the booking cards
      try {
        const r = await axios.get(`${API}/admin/payments/by-ticket/${ticket_id}`, apiAuth());
        setTicketPayments(r.data || []);
      } catch (err) { console.error("Failed to refresh ticket payments after sync:", err); }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncBusy(false);
    }
  };

  // ----- Login screen -----
  if (!authed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#0A0A0A]" data-testid="admin-login">
        <Link to="/" className="absolute top-6 left-6 flex items-center gap-2">
          <LogoMark size="h-8" />
        </Link>
        <div className="w-full max-w-md bg-[#121212] border border-zinc-800 rounded-md p-8">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">ADMIN ACCESS</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter mb-6">Sign in</h1>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF] mb-4"
            onKeyDown={(e) => e.key === "Enter" && login()}
            data-testid="admin-token-input"
            autoComplete="off"
          />
          <label
            className="flex items-center gap-2 mb-4 cursor-pointer text-sm text-zinc-300 select-none"
            data-testid="admin-remember-label"
          >
            <input
              type="checkbox"
              checked={rememberToken}
              onChange={(e) => setRememberToken(e.target.checked)}
              className="w-4 h-4 accent-[#0055FF] cursor-pointer"
              data-testid="admin-remember-checkbox"
            />
            Remember on this device
          </label>
          <Button
            onClick={login}
            className="w-full bg-[#0055FF] hover:bg-blue-600 text-white rounded-md font-semibold"
            data-testid="admin-login-btn"
          >
            Sign in
          </Button>
          <p className="text-zinc-500 text-xs mt-6 font-mono leading-relaxed">
            Token is stored in <span className="text-zinc-300">backend/.env</span> as <span className="text-[#FFCC00]">ADMIN_TOKEN</span>.
            By default you'll re-enter it each visit — check "Remember" only on personal devices.
          </p>
        </div>
      </div>
    );
  }

  const pending = reviews.filter((r) => !r.approved);
  const published = reviews.filter((r) => r.approved);

  // ----- Search helpers -----
  const normalizePhone = (s) => (s || "").replace(/\D/g, "");
  const matchesQuery = (b, q) => {
    if (!q) return true;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const phoneNeedle = normalizePhone(needle);
    const fields = [
      b.ticket_id, b.customer_name, b.email, b.device_type,
      b.service_type, b.status, b.delivery_method, b.issue_description, b.notes,
    ];
    if (fields.some((v) => (v || "").toString().toLowerCase().includes(needle))) return true;
    if (phoneNeedle && phoneNeedle.length >= 3 && normalizePhone(b.phone).includes(phoneNeedle)) return true;
    return false;
  };
  const reviewMatchesQuery = (r, q) => {
    if (!q) return true;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return [r.id, r.customer_name, r.device_repaired, r.comment]
      .some((v) => (v || "").toString().toLowerCase().includes(needle));
  };

  const filteredBookings = bookings.filter((b) => matchesQuery(b, bookingSearch));
  const filteredPending = pending.filter((r) => reviewMatchesQuery(r, reviewSearch));
  const filteredPublished = published.filter((r) => reviewMatchesQuery(r, reviewSearch));
  const filteredReviewIds = new Set([
    ...filteredPending.map((r) => r.id),
    ...filteredPublished.map((r) => r.id),
  ]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-zinc-900 bg-black/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <LogoMark size="h-10" />
            <div className="hidden sm:block font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase border-l border-zinc-800 pl-4">
              Control Center
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowTokenHelp(true)}
              variant="outline"
              size="sm"
              className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
              data-testid="admin-help-token-btn"
            >
              <Info className="w-4 h-4 mr-1" /> Change token
            </Button>
            <Button
              onClick={loadAll}
              variant="outline"
              size="sm"
              className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        {/* Environment status banner */}
        {envInfo && (envInfo.warnings?.length > 0 || envInfo.is_preview_db || envInfo.is_test_stripe) && (
          <div
            className={`mb-8 rounded-md p-4 border ${
              envInfo.is_preview_db ? "bg-[#0055FF]/10 border-[#0055FF]/40" : "bg-[#FFCC00]/10 border-[#FFCC00]/40"
            }`}
            data-testid="admin-env-banner"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">
                  ENVIRONMENT STATUS
                </div>
                <div className="flex flex-wrap gap-2 text-xs font-mono">
                  <span className={`px-2 py-1 rounded-sm border ${
                    envInfo.is_preview_db ? "bg-[#0055FF]/15 text-[#0055FF] border-[#0055FF]/30" : "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30"
                  }`}>
                    DB · {envInfo.db_name}
                  </span>
                  <span className={`px-2 py-1 rounded-sm border ${
                    envInfo.is_test_stripe ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/30" : "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30"
                  }`}>
                    STRIPE · {envInfo.stripe_mode.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded-sm border ${
                    envInfo.email_configured ? "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30" : "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30"
                  }`}>
                    EMAIL · {envInfo.email_configured ? "READY" : "MISSING"}
                  </span>
                </div>
              </div>
            </div>
            {envInfo.warnings?.length > 0 && (
              <ul className="mt-3 pt-3 border-t border-white/5 space-y-1 text-xs text-zinc-300">
                {envInfo.warnings.map((w, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[#FFCC00]">⚠</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-10 border-b border-zinc-900">
          <button
            onClick={() => setTab("bookings")}
            className={`px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase border-b-2 transition-colors ${
              tab === "bookings" ? "border-[#0055FF] text-white" : "border-transparent text-zinc-500 hover:text-white"
            }`}
            data-testid="admin-tab-bookings"
          >
            <span className="flex items-center gap-2"><Package className="w-4 h-4" /> Bookings <span className="text-zinc-600">{bookings.length}</span></span>
          </button>
          <button
            onClick={() => setTab("reviews")}
            className={`px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase border-b-2 transition-colors ${
              tab === "reviews" ? "border-[#0055FF] text-white" : "border-transparent text-zinc-500 hover:text-white"
            }`}
            data-testid="admin-tab-reviews"
          >
            <span className="flex items-center gap-2">
              <MessageSquareQuote className="w-4 h-4" /> Reviews
              {pending.length > 0 && (
                <span className="bg-[#FFCC00] text-black px-1.5 py-0.5 rounded-sm text-[10px]">
                  {pending.length} pending
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setTab("gallery")}
            className={`px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase border-b-2 transition-colors ${
              tab === "gallery" ? "border-[#0055FF] text-white" : "border-transparent text-zinc-500 hover:text-white"
            }`}
            data-testid="admin-tab-gallery"
          >
            <span className="flex items-center gap-2">
              <Images className="w-4 h-4" /> Gallery <span className="text-zinc-600">{gallery.length}</span>
            </span>
          </button>
        </div>

        {/* Bookings tab */}
        {tab === "bookings" && (
          <>
            <div className="mb-8">
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">REPAIR QUEUE</div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
                {bookingSearch.trim()
                  ? `${filteredBookings.length} of ${bookings.length} ticket${bookings.length !== 1 ? "s" : ""}`
                  : `${bookings.length} ticket${bookings.length !== 1 ? "s" : ""}`}
              </h1>
            </div>

            <div className="mb-4 relative" data-testid="admin-bookings-search-wrap">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                type="text"
                value={bookingSearch}
                onChange={(e) => setBookingSearch(e.target.value)}
                placeholder={bookings.length === 0 ? "Search will activate once you have tickets…" : "Search by ticket, name, email, phone, device, status…"}
                disabled={bookings.length === 0}
                className="bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 pl-9 pr-9 h-11 focus:border-[#0055FF] focus:ring-[#0055FF] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-bookings-search"
                aria-label="Search bookings"
              />
              {bookingSearch && (
                <button
                  type="button"
                  onClick={() => setBookingSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  data-testid="admin-bookings-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div
              className="mb-4 flex flex-wrap items-center gap-3 bg-[#121212] border border-zinc-800 rounded-md px-4 py-3"
              data-testid="admin-bookings-bulk-toolbar"
            >
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={selectAllBookings}
                disabled={filteredBookings.length === 0}
                className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-bookings-select-all"
              >
                <Check className="w-4 h-4 mr-1" />
                {filteredBookings.length > 0 && filteredBookings.every((b) => selectedBookings.has(b.ticket_id))
                  ? "Clear selection"
                  : bookingSearch.trim() ? "Select filtered" : "Select all"}
              </Button>
              <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500">
                {selectedBookings.size} of {bookingSearch.trim() ? `${filteredBookings.length} (filtered)` : bookings.length} selected
              </span>
              <div className="flex-1" />
              <Button
                type="button"
                size="sm"
                onClick={bulkDeleteBookings}
                disabled={selectedBookings.size === 0}
                className="bg-[#FF3B30] hover:bg-[#d92e25] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-bookings-bulk-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete selected
              </Button>
            </div>

            <div className="space-y-3" data-testid="admin-bookings-list">
              {bookings.length === 0 && !loading && (
                <div className="bg-[#121212] border border-zinc-800 rounded-md p-12 text-center text-zinc-500">
                  No bookings yet. Customer requests will appear here.
                </div>
              )}

              {bookings.length > 0 && filteredBookings.length === 0 && (
                <div
                  className="bg-[#121212] border border-zinc-800 rounded-md p-12 text-center text-zinc-500"
                  data-testid="admin-bookings-no-results"
                >
                  No tickets match <span className="text-white font-mono">"{bookingSearch}"</span>.
                  <button
                    type="button"
                    onClick={() => setBookingSearch("")}
                    className="block mx-auto mt-3 text-[#0055FF] hover:text-white text-sm underline"
                  >
                    Clear search
                  </button>
                </div>
              )}

              {filteredBookings.map((b) => {
                const isEditing = editing === b.ticket_id;
                const isSelected = selectedBookings.has(b.ticket_id);
                return (
                  <div
                    key={b.ticket_id}
                    className={`bg-[#121212] border rounded-md p-6 transition-colors ${isSelected ? "border-[#0055FF]/60" : "border-zinc-800"}`}
                    data-testid={`admin-booking-${b.ticket_id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleBookingSelected(b.ticket_id)}
                          className="mt-2 w-4 h-4 accent-[#0055FF] cursor-pointer"
                          data-testid={`admin-booking-checkbox-${b.ticket_id}`}
                          aria-label={`Select ticket ${b.ticket_id}`}
                        />
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono font-bold text-[#FFCC00] text-lg">{b.ticket_id}</span>
                            <span className={`px-2 py-0.5 rounded-sm border font-mono text-[10px] tracking-[0.15em] uppercase ${STATUS_COLORS[b.status] || ""}`}>
                              {b.status}
                            </span>
                          </div>
                          <div className="font-display text-xl">{b.customer_name}</div>
                          <div className="text-sm text-zinc-400 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                            <a href={`mailto:${b.email}`} className="hover:text-white">{b.email}</a>
                            <a href={`tel:${b.phone}`} className="hover:text-white">{b.phone}</a>
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-xs font-mono text-zinc-500">
                        <div>CREATED · {new Date(b.created_at).toLocaleString()}</div>
                        <div>UPDATED · {new Date(b.updated_at).toLocaleString()}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm mb-4">
                      <div>
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Device</div>
                        <div className="text-white">{b.device_type}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Service</div>
                        <div className="text-white">{b.service_type}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Delivery</div>
                        <div className="text-white capitalize">{b.delivery_method.replace("-", " ")}</div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Issue</div>
                      <div className="text-zinc-300 leading-relaxed">{b.issue_description}</div>
                    </div>

                    {b.photos && b.photos.length > 0 && (
                      <div className="mb-4">
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2 flex items-center gap-2">
                          <ImageIcon className="w-3 h-3" /> Photos ({b.photos.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {b.photos.map((src, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setLightbox(src)}
                              className="w-20 h-20 rounded-md overflow-hidden border border-zinc-800 hover:border-[#0055FF] transition-colors"
                              data-testid={`admin-photo-${b.ticket_id}-${idx}`}
                            >
                              <img src={src} alt={`${b.ticket_id}-${idx}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {b.preferred_date && (
                      <div className="mb-4 text-sm">
                        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mr-2">Preferred date:</span>
                        <span className="text-white">{b.preferred_date}</span>
                      </div>
                    )}

                    {!isEditing ? (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-zinc-800 pt-4">
                        <div className="text-sm text-zinc-400 flex items-center gap-3 flex-wrap">
                          {b.notes ? <><span className="text-zinc-500 font-mono text-xs uppercase">Notes ·</span> {b.notes}</> : <span className="text-zinc-600 italic">No notes</span>}
                          {b.deposit_paid && (
                            <span className="bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30 font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm">
                              ✓ Deposit paid
                            </span>
                          )}
                          {b.invoice_paid && (
                            <span
                              className="bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30 font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm"
                              data-testid={`admin-invoice-paid-badge-${b.ticket_id}`}
                              title={b.invoice_paid_at ? `Paid ${new Date(b.invoice_paid_at).toLocaleString()}` : "Invoice paid"}
                            >
                              ✓ Invoice paid{b.invoice_amount_usd != null ? ` · $${b.invoice_amount_usd.toFixed(2)}` : ""}
                            </span>
                          )}
                          {b.deposit_required && !b.deposit_paid && (
                            <span className="bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/30 font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm">
                              ⏳ Awaiting deposit
                            </span>
                          )}
                          {b.refund_issued && (
                            <span className="bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/30 font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm">
                              ↩ Refunded
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            onClick={() => openInvoice(b.ticket_id)}
                            variant="outline"
                            size="sm"
                            className="bg-transparent border-[#0055FF]/40 text-[#0055FF] hover:bg-[#0055FF]/10 hover:text-[#0055FF]"
                            data-testid={`admin-invoice-${b.ticket_id}`}
                          >
                            <DollarSign className="w-3 h-3 mr-1" /> Send invoice
                          </Button>
                          <Button
                            onClick={() => syncTicketPayments(b.ticket_id)}
                            disabled={syncBusy}
                            variant="outline"
                            size="sm"
                            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-white/5 hover:text-white"
                            data-testid={`admin-sync-${b.ticket_id}`}
                            title="Pull latest payment status from Stripe"
                          >
                            <RefreshCw className={`w-3 h-3 mr-1 ${syncBusy ? "animate-spin" : ""}`} /> Sync payments
                          </Button>
                          {(b.deposit_paid || b.invoice_paid) && (
                            <Button
                              onClick={() => openRefund(b.ticket_id)}
                              variant="outline"
                              size="sm"
                              className="bg-transparent border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10 hover:text-[#FFCC00]"
                              data-testid={`admin-refund-${b.ticket_id}`}
                            >
                              <RotateCcw className="w-3 h-3 mr-1" /> Refund
                            </Button>
                          )}
                          <Button
                            onClick={() => startEdit(b)}
                            variant="outline"
                            size="sm"
                            className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
                            data-testid={`admin-edit-${b.ticket_id}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" /> Update
                          </Button>
                          <Button
                            onClick={() => deleteBooking(b.ticket_id)}
                            variant="outline"
                            size="sm"
                            className="bg-transparent border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"
                            data-testid={`admin-delete-${b.ticket_id}`}
                            aria-label={`Delete ticket ${b.ticket_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t border-zinc-800 pt-4 space-y-3">
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Status</div>
                          <Select value={editStatus} onValueChange={setEditStatus}>
                            <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 text-white" data-testid={`admin-status-select-${b.ticket_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-zinc-800 text-white">
                              {statuses.map((s) => (
                                <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Notes (visible to customer)</div>
                          <Textarea
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            rows={3}
                            className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF] resize-none"
                            data-testid={`admin-notes-${b.ticket_id}`}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={saveEdit}
                            className="bg-[#0055FF] hover:bg-blue-600 text-white"
                            data-testid={`admin-save-${b.ticket_id}`}
                          >
                            <Save className="w-4 h-4 mr-1" /> Save
                          </Button>
                          <Button
                            onClick={() => setEditing(null)}
                            variant="outline"
                            className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
                          >
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Reviews tab */}
        {tab === "reviews" && (
          <>
            <div className="mb-8">
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">REVIEW MODERATION</div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
                {reviewSearch.trim()
                  ? `${filteredPending.length} pending · ${filteredPublished.length} published (filtered)`
                  : `${pending.length} pending · ${published.length} published`}
              </h1>
              <p className="text-zinc-500 text-sm mt-3 max-w-xl">
                New reviews arrive with status <span className="text-[#FFCC00]">pending</span>. Click
                Approve to publish them on the public site, or Delete to remove.
              </p>
            </div>

            <div className="mb-4 relative" data-testid="admin-reviews-search-wrap">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                type="text"
                value={reviewSearch}
                onChange={(e) => setReviewSearch(e.target.value)}
                placeholder={reviews.length === 0 ? "Search will activate once you have reviews…" : "Search by name, device, or comment text…"}
                disabled={reviews.length === 0}
                className="bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 pl-9 pr-9 h-11 focus:border-[#0055FF] focus:ring-[#0055FF] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-reviews-search"
                aria-label="Search reviews"
              />
              {reviewSearch && (
                <button
                  type="button"
                  onClick={() => setReviewSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  data-testid="admin-reviews-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div
              className="mb-4 flex flex-wrap items-center gap-3 bg-[#121212] border border-zinc-800 rounded-md px-4 py-3"
              data-testid="admin-reviews-bulk-toolbar"
            >
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={selectAllReviews}
                disabled={filteredReviewIds.size === 0}
                className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-reviews-select-all"
              >
                <Check className="w-4 h-4 mr-1" />
                {filteredReviewIds.size > 0 && [...filteredReviewIds].every((id) => selectedReviews.has(id))
                  ? "Clear selection"
                  : reviewSearch.trim() ? "Select filtered" : "Select all"}
              </Button>
              <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500">
                {selectedReviews.size} of {reviewSearch.trim() ? `${filteredReviewIds.size} (filtered)` : reviews.length} selected
              </span>
              <div className="flex-1" />
              <Button
                type="button"
                size="sm"
                onClick={bulkDeleteReviews}
                disabled={selectedReviews.size === 0}
                className="bg-[#FF3B30] hover:bg-[#d92e25] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-reviews-bulk-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete selected
              </Button>
            </div>

            {reviews.length > 0 && filteredReviewIds.size === 0 && (
              <div
                className="bg-[#121212] border border-zinc-800 rounded-md p-12 text-center text-zinc-500 mb-6"
                data-testid="admin-reviews-no-results"
              >
                No reviews match <span className="text-white font-mono">"{reviewSearch}"</span>.
                <button
                  type="button"
                  onClick={() => setReviewSearch("")}
                  className="block mx-auto mt-3 text-[#0055FF] hover:text-white text-sm underline"
                >
                  Clear search
                </button>
              </div>
            )}

            {filteredPending.length > 0 && (
              <div className="mb-10">
                <h2 className="font-mono text-xs tracking-[0.25em] uppercase text-[#FFCC00] mb-3">PENDING APPROVAL</h2>
                <div className="space-y-3" data-testid="admin-reviews-pending">
                  {filteredPending.map((r) => (
                    <ReviewCard
                      key={r.id}
                      r={r}
                      onApprove={() => setApproval(r.id, true)}
                      onDelete={() => deleteReview(r.id)}
                      isPending
                      isSelected={selectedReviews.has(r.id)}
                      onToggleSelect={() => toggleReviewSelected(r.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">PUBLISHED</h2>
              <div className="space-y-3" data-testid="admin-reviews-published">
                {filteredPublished.length === 0 ? (
                  <div className="bg-[#121212] border border-zinc-800 rounded-md p-8 text-center text-zinc-500">
                    {reviewSearch.trim() ? "No published reviews match your search." : "No reviews published yet."}
                  </div>
                ) : (
                  filteredPublished.map((r) => (
                    <ReviewCard
                      key={r.id}
                      r={r}
                      onUnpublish={() => setApproval(r.id, false)}
                      onDelete={() => deleteReview(r.id)}
                      isSelected={selectedReviews.has(r.id)}
                      onToggleSelect={() => toggleReviewSelected(r.id)}
                    />
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Gallery tab */}
        {tab === "gallery" && (
          <GalleryAdmin
            items={gallery}
            apiAuth={apiAuth}
            onChanged={loadAll}
          />
        )}
      </main>

      {/* Photo lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="bg-black border-zinc-800 max-w-4xl p-2">
          <DialogTitle className="sr-only">Booking photo preview</DialogTitle>
          <DialogDescription className="sr-only">Full-size view of the uploaded booking photo.</DialogDescription>
          {lightbox && <img src={lightbox} alt="preview" className="w-full h-auto rounded-md" />}
        </DialogContent>
      </Dialog>

      {/* Change token help */}
      <Dialog open={showTokenHelp} onOpenChange={setShowTokenHelp}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg">
          <DialogTitle className="font-display font-bold text-2xl tracking-tight">Change your admin token</DialogTitle>
          <DialogDescription className="sr-only">Step-by-step instructions for rotating the admin token.</DialogDescription>
          <div className="space-y-4">
            <ol className="text-sm text-zinc-300 space-y-3 list-decimal list-inside">
              <li>Open the file <code className="bg-black px-1.5 py-0.5 rounded text-[#FFCC00]">/app/backend/.env</code></li>
              <li>Find the line:<br/><code className="bg-black px-2 py-1 rounded text-xs block mt-1">ADMIN_TOKEN="tombs-admin-2025"</code></li>
              <li>Replace it with a long, random secret (at least 20 characters):<br/><code className="bg-black px-2 py-1 rounded text-xs block mt-1">ADMIN_TOKEN="your-new-secret-here"</code></li>
              <li>Run <code className="bg-black px-1.5 py-0.5 rounded text-[#34C759]">sudo supervisorctl restart backend</code></li>
              <li>Log out here and sign back in with the new token.</li>
            </ol>
            <p className="text-zinc-500 text-xs font-mono">
              Tip: You can ask me to do this for you — just tell me the new token you want.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send invoice dialog */}
      <Dialog open={!!invoiceFor} onOpenChange={(v) => !v && setInvoiceFor(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg">
          <DialogTitle className="font-display font-bold text-2xl tracking-tight">Send invoice</DialogTitle>
          <DialogDescription className="sr-only">Send a Stripe checkout link to the customer for this ticket.</DialogDescription>
          <div className="space-y-4">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">
              Ticket <span className="text-[#FFCC00]">{invoiceFor}</span>
            </div>

            <div>
              <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Amount (USD)</label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={invoiceAmount}
                onChange={(e) => setInvoiceAmount(e.target.value)}
                placeholder="125.00"
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
                data-testid="invoice-amount"
              />
            </div>

            <div>
              <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">What's this for?</label>
              <Input
                value={invoiceLabel}
                onChange={(e) => setInvoiceLabel(e.target.value)}
                placeholder="PS5 disc drive replacement"
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
                data-testid="invoice-label"
              />
            </div>

            {ticketPayments.length > 0 && (
              <div className="border-t border-zinc-800 pt-4 space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">PAST PAYMENTS</div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => invoiceFor && syncTicketPayments(invoiceFor)}
                    disabled={syncBusy}
                    className="h-7 text-xs bg-transparent border-zinc-700 text-zinc-300 hover:bg-white/5 hover:text-white"
                    data-testid="admin-invoice-dialog-sync"
                    title="Pull latest payment status from Stripe"
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${syncBusy ? "animate-spin" : ""}`} /> Sync from Stripe
                  </Button>
                </div>
                {ticketPayments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-300">
                      ${p.amount_usd.toFixed(2)} · {p.label}
                    </span>
                    <span className={`font-mono uppercase tracking-[0.15em] ${
                      p.payment_status === "paid" ? "text-[#34C759]" : "text-[#FFCC00]"
                    }`}>
                      {p.payment_status}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setInvoiceFor(null)} className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white">
                Cancel
              </Button>
              <Button
                onClick={sendInvoice}
                disabled={invoiceBusy}
                className="bg-[#0055FF] hover:bg-blue-600 text-white font-semibold"
                data-testid="invoice-send-btn"
              >
                {invoiceBusy ? "Sending…" : "Email link to customer"}
              </Button>
            </div>
            <p className="font-mono text-[10px] text-zinc-600 text-center">
              Customer gets an email with a secure Stripe checkout link.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={!!refundFor} onOpenChange={(v) => !v && setRefundFor(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg">
          <DialogTitle className="font-display font-bold text-2xl tracking-tight">Refund customer</DialogTitle>
          <DialogDescription className="sr-only">Refund a paid deposit or invoice on this ticket.</DialogDescription>
          {refundFor && (
            <div className="space-y-4">
              <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">
                Ticket <span className="text-[#FFCC00]">{refundFor.ticket_id}</span>
              </div>

              <div className="bg-[#0A0A0A] border border-zinc-800 rounded-md p-4 space-y-2 text-sm" data-testid="refund-summary">
                <div className="flex justify-between text-zinc-400"><span>Customer paid</span><span className="text-white font-mono">${refundFor.txn.amount_usd.toFixed(2)}</span></div>
                {refundFor.isShipIn ? (
                  <>
                    <div className="flex justify-between text-zinc-400"><span>Diagnostic fee (ship-in policy)</span><span className="text-[#FFCC00] font-mono">−$15.00</span></div>
                    <div className="flex justify-between border-t border-zinc-800 pt-2 mt-2"><span className="text-white font-semibold">Default refund</span><span className="text-[#34C759] font-mono font-bold">${Math.max(0, refundFor.txn.amount_usd - 15).toFixed(2)}</span></div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-zinc-500 text-xs italic"><span>No diagnostic fee — drop-off / in-person</span><span className="font-mono">$0.00</span></div>
                    <div className="flex justify-between border-t border-zinc-800 pt-2 mt-2"><span className="text-white font-semibold">Default refund</span><span className="text-[#34C759] font-mono font-bold">${refundFor.txn.amount_usd.toFixed(2)}</span></div>
                  </>
                )}
              </div>

              <div>
                <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Refund amount (USD)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#FFCC00] focus:ring-[#FFCC00]"
                  data-testid="refund-amount"
                />
                <p className="font-mono text-[10px] text-zinc-600 mt-1">
                  No cap — Stripe refunds up to ${refundFor.txn.amount_usd.toFixed(2)}; anything above is recorded as owed manually (Venmo / cash / separate transfer).
                </p>
                {parseFloat(refundAmount) > refundFor.txn.amount_usd && (
                  <p className="font-mono text-[11px] text-[#FFCC00] mt-2" data-testid="refund-overpay-warning">
                    ⚠ Stripe will refund ${refundFor.txn.amount_usd.toFixed(2)} · ${(parseFloat(refundAmount) - refundFor.txn.amount_usd).toFixed(2)} owed manually.
                  </p>
                )}
              </div>

              <div>
                <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Reason (shown to customer)</label>
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={2}
                  className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#FFCC00] focus:ring-[#FFCC00] resize-none"
                  data-testid="refund-reason"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRefundFor(null)} className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white">
                  Cancel
                </Button>
                <Button
                  onClick={sendRefund}
                  disabled={refundBusy}
                  className="bg-[#FFCC00] hover:bg-yellow-500 text-black font-semibold"
                  data-testid="refund-confirm-btn"
                >
                  {refundBusy ? "Processing…" : "Confirm refund"}
                </Button>
              </div>
              <p className="font-mono text-[10px] text-zinc-600 text-center">
                Customer will be emailed automatically. Test key: refund tracked in our system; finalize in Stripe Dashboard once your live key is set.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewCard({ r, onApprove, onUnpublish, onDelete, isPending = false, isSelected = false, onToggleSelect }) {
  return (
    <div
      className={`bg-[#121212] border rounded-md p-6 transition-colors ${
        isSelected ? "border-[#0055FF]/60" : isPending ? "border-[#FFCC00]/40" : "border-zinc-800"
      }`}
      data-testid={`admin-review-${r.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className="mt-1 w-4 h-4 accent-[#0055FF] cursor-pointer"
              data-testid={`admin-review-checkbox-${r.id}`}
              aria-label={`Select review by ${r.customer_name}`}
            />
          )}
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Stars value={r.rating} />
              <span className="font-display font-semibold">{r.customer_name}</span>
              <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500">
                {r.device_repaired}
              </span>
            </div>
            <div className="font-mono text-[10px] text-zinc-600">
              {new Date(r.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isPending && onApprove && (
            <Button
              onClick={onApprove}
              size="sm"
              className="bg-[#34C759] hover:bg-[#2ea64a] text-black font-semibold"
              data-testid={`admin-review-approve-${r.id}`}
            >
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
          )}
          {!isPending && onUnpublish && (
            <Button
              onClick={onUnpublish}
              size="sm"
              variant="outline"
              className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
              data-testid={`admin-review-unpublish-${r.id}`}
            >
              Unpublish
            </Button>
          )}
          <Button
            onClick={onDelete}
            size="sm"
            variant="outline"
            className="bg-transparent border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"
            data-testid={`admin-review-delete-${r.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <p className="text-zinc-300 leading-relaxed">{r.comment}</p>
    </div>
  );
}


function GalleryAdmin({ items, apiAuth, onChanged }) {
  const [form, setForm] = useState({ title: "", caption: "", device: "", image: "", before_image: "" });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const onPickImage = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await fileToCompressedDataUrl(file, { maxWidth: 1600, quality: 0.78 });
      setForm((f) => ({ ...f, [key]: data }));
    } catch {
      toast.error("Could not process image");
    } finally {
      e.target.value = "";
    }
  };

  const reset = () => {
    setForm({ title: "", caption: "", device: "", image: "", before_image: "" });
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image) {
      toast.error("Title and an after photo are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        caption: form.caption.trim() || null,
        device: form.device.trim() || null,
        image: form.image,
        before_image: form.before_image || null,
      };
      await axios.post(`${API}/admin/gallery`, payload, apiAuth());
      toast.success("Posted to gallery");
      reset();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gallery post permanently?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, apiAuth());
      toast.success("Deleted");
      onChanged();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">REPAIR GALLERY</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
            {items.length} post{items.length !== 1 ? "s" : ""}
          </h1>
          <p className="text-zinc-500 text-sm mt-3 max-w-xl">
            Show off real repairs you've done. Each post can include an optional &quot;before&quot; photo for a slick before/after toggle on the public gallery.
          </p>
        </div>
        <Button
          onClick={() => setShowForm((s) => !s)}
          className="bg-[#0055FF] hover:bg-blue-600 text-white font-semibold"
          data-testid="admin-gallery-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> {showForm ? "Cancel" : "New post"}
        </Button>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          className="bg-[#121212] border border-zinc-800 rounded-md p-6 sm:p-8 mb-10 grid grid-cols-1 md:grid-cols-2 gap-6"
          data-testid="admin-gallery-form"
        >
          <div className="md:col-span-2">
            <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Title *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="PS5 disc drive replacement"
              className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
              data-testid="admin-gallery-title"
            />
          </div>

          <div>
            <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Device (optional)</label>
            <Input
              value={form.device}
              onChange={(e) => setForm({ ...form, device: e.target.value })}
              placeholder="PS5, Xbox, Switch, PC..."
              className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
              data-testid="admin-gallery-device"
            />
          </div>

          <div>
            <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Caption (optional)</label>
            <Input
              value={form.caption}
              onChange={(e) => setForm({ ...form, caption: e.target.value })}
              placeholder="Customer dropped this off Sunday, picked up Tuesday..."
              className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
              data-testid="admin-gallery-caption"
            />
          </div>

          <div>
            <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">After photo *</label>
            {form.image ? (
              <div className="relative rounded-md overflow-hidden border border-zinc-800">
                <img src={form.image} alt="after" className="w-full h-48 object-cover" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, image: "" })}
                  className="absolute top-2 right-2 bg-black/80 hover:bg-[#FF3B30] text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-48 rounded-md border-2 border-dashed border-zinc-800 hover:border-[#0055FF] cursor-pointer transition-colors text-zinc-500 hover:text-white" data-testid="admin-gallery-after-drop">
                <Upload className="w-6 h-6 mb-2" />
                <span className="font-mono text-xs tracking-[0.15em] uppercase">Choose photo</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(e, "image")} />
              </label>
            )}
          </div>

          <div>
            <label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Before photo (optional)</label>
            {form.before_image ? (
              <div className="relative rounded-md overflow-hidden border border-zinc-800">
                <img src={form.before_image} alt="before" className="w-full h-48 object-cover" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, before_image: "" })}
                  className="absolute top-2 right-2 bg-black/80 hover:bg-[#FF3B30] text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-48 rounded-md border-2 border-dashed border-zinc-800 hover:border-[#0055FF] cursor-pointer transition-colors text-zinc-500 hover:text-white" data-testid="admin-gallery-before-drop">
                <Upload className="w-6 h-6 mb-2" />
                <span className="font-mono text-xs tracking-[0.15em] uppercase">Choose photo</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(e, "before_image")} />
              </label>
            )}
          </div>

          <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={reset} className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-blue-600 text-white font-semibold" data-testid="admin-gallery-submit">
              {busy ? "Uploading..." : <><Upload className="w-4 h-4 mr-1" /> Post</>}
            </Button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-gallery-grid">
        {items.length === 0 ? (
          <div className="col-span-full bg-[#121212] border border-zinc-800 rounded-md p-12 text-center text-zinc-500">
            <Images className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            No gallery posts yet. Hit &quot;New post&quot; to add your first repair photo.
          </div>
        ) : (
          items.map((it) => (
            <div
              key={it.id}
              className="bg-[#121212] border border-zinc-800 rounded-md overflow-hidden"
              data-testid={`admin-gallery-${it.id}`}
            >
              <img src={it.image} alt={it.title} className="w-full h-48 object-cover" />
              <div className="p-4">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <h3 className="font-display font-semibold leading-tight">{it.title}</h3>
                  {it.device && (
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#FFCC00]">{it.device}</span>
                  )}
                </div>
                {it.caption && <p className="text-zinc-400 text-sm leading-relaxed mb-3">{it.caption}</p>}
                <div className="flex items-center justify-between border-t border-zinc-800 pt-3 mt-2">
                  <span className="font-mono text-[10px] text-zinc-600">
                    {new Date(it.created_at).toLocaleDateString()}
                    {it.before_image ? " · before/after" : ""}
                  </span>
                  <Button
                    onClick={() => remove(it.id)}
                    size="sm"
                    variant="outline"
                    className="bg-transparent border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"
                    data-testid={`admin-gallery-delete-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
